import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, Flip } from 'react-toastify';
import api from '../api';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [error, setError] = useState(''); // Fixed incomplete useState
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    token: null,
    loading: true,
  });
  const [user, setUser] = useState({});
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

//const axiUrl = 'http://localhost:5003'
const axiUrl = 'https://lightningpresort-3c0bfe3acad3.herokuapp.com'
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.post('/user/profile', {
          withCredentials: true,
        });
        console.log('Fetched user info on mount:', response.data);
        if (response.data.status === 200) {
          
  console.log(user)
          setAuthState({ isAuthenticated: true, token: null, loading: false });
          setUser(response.data?.user || {});
          setError('');
        } else {
          console.log('Auth check failed with status:', response.data.status);
          setAuthState({ isAuthenticated: false, token: null, loading: false });
          setUser({});
          setError('');
        }
      } catch (error) {
        console.error('Auth check error:', error.response?.data || error.message);
        setAuthState({ isAuthenticated: false, token: null, loading: false });
        setUser({});
        setError('');
      }
    };
    checkAuth();
  }, []);

  console.log(user)
  const refreshJwt = async () => {
    if (isLoggingOut) {
      console.log('Skipping JWT refresh: logout in progress');
      return;
    }
    if (!authState.isAuthenticated) {
      console.log('Skipping JWT refresh: not authenticated');
      return;
    }
    try {
      //console.log('Refreshing JWT');
      const refreshResponse = await api.post('/refresh', {}, { withCredentials: true });
      //console.log('JWT refreshed:', refreshResponse.data);
      const profileResponse = await api.post('/user/profile', { withCredentials: true });
      if (profileResponse.data.status === 200) {
        setAuthState({ isAuthenticated: true, token: null, loading: false });
        setUser(profileResponse.data?.user || {});
        setError('');
      } else {
        throw new Error(`Profile fetch failed after refresh with status: ${profileResponse.data.status}`);
      }
    } catch (error) {
      console.error('JWT refresh error:', error.response?.data || error.message);
      await logout();
      console.log('Navigating to / after refresh failure');
      navigate('/');
    }
  };

  useEffect(() => {
    let timer;

    const handleInteraction = () => {
      if (isLoggingOut) {
        console.log('Ignoring interaction: logout in progress');
        return;
      }
      if (authState.isAuthenticated) {
        console.log('User interaction detected, resetting JWT');
        refreshJwt();
      } else {
        console.log('Ignoring interaction: not authenticated');
      }
    };

    if (authState.isAuthenticated && !isLoggingOut) {
      console.log('Starting auto-refresh timer');
      timer = setTimeout(async () => {
        if (isLoggingOut) {
          console.log('Auto-refresh check skipped: logout in progress');
          return;
        }
        try {
          const response = await api.post('/user/profile', { withCredentials: true });
          console.log('Auto-refresh check - Profile response:', response.data);
          if (response.data.status !== 200) {
            console.log('Auto-refresh: Authentication invalid');
            await logout();
            console.log('Navigating to / after auto-logout');
            navigate('/');
          }
        } catch (error) {
          console.error('Auto-refresh: Server error', error.response?.data || error.message);
          await logout();
          console.log('Navigating to / after auto-logout');
          navigate('/');
        }
        timer = setInterval(async () => {
          if (isLoggingOut) return;
          try {
            const response = await api.post('/user/profile', { withCredentials: true });
            console.log('Auto-refresh check - Profile response:', response.data);
            if (response.data.status !== 200) {
              console.log('Auto-refresh: Authentication invalid');
              await logout();
              navigate('/');
            }
          } catch (error) {
            console.error('Auto-refresh: Server error', error.response?.data || error.message);
            await logout();
            navigate('/');
          }
        }, 300000); // 5 minutes
      }, 120000); // Increased to 2-minute initial delay

      window.addEventListener('click', handleInteraction);

      console.log('Location changed:', location.pathname);
      if (authState.isAuthenticated) {
        console.log('Navigation detected, resetting JWT');
        refreshJwt();
      } else {
        console.log('Ignoring navigation: not authenticated');
      }
    }

    return () => {
      console.log('Cleaning up timer and click listener');
      clearTimeout(timer);
      clearInterval(timer);
      window.removeEventListener('click', handleInteraction);
    };
  }, [authState.isAuthenticated, location, navigate, isLoggingOut]);

  const login = async (credentials) => {
    console.log('Sending login request:', credentials);
    setAuthState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await api.post('/user/login', credentials, { withCredentials: true });
      console.log('Login response:', response.data);
      if (response.data.status === 200) {
        const profileResponse = await api.post('/user/profile', { withCredentials: true });
        console.log('Profile response after login:', profileResponse.data);
        if (profileResponse.data.status === 200) {
          setAuthState({ isAuthenticated: true, token: null, loading: false });
          setUser(profileResponse.data?.user || {});
          setError('');
          localStorage.setItem('email',credentials.email)
          console.log('Navigating to /dashboard');
          navigate('/dashboard');
        } else {
          console.error('Profile fetch failed after login with status:', profileResponse.data.status, 'Message:', profileResponse.data.message);
          setAuthState({ isAuthenticated: false, token: null, loading: false });
          setUser({});
          setError('Failed to fetch profile after login');
          // Avoid immediate logout; let user retry
        }
      } else if (response.data.status === 423) {
        setAuthState({ isAuthenticated: false, token: null, loading: false });
        setUser({});
        setError('Account Inactive');
      } else if (response.data.status === 400) {
        setAuthState({ isAuthenticated: false, token: null, loading: false });
        setUser({});
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      setAuthState({ isAuthenticated: false, token: null, loading: false });
      setUser({});
      setError(error.response?.data?.message || 'Unknown error');
    }
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      console.log('Sending logout request');
      const response = await api.post('/logout', {}, { withCredentials: true });
      console.log('Logout response:', response.data);
      if (response.data.status === 'success') {
        console.log('Logout successful');
      } else {
        console.warn('Unexpected logout response:', response.data);
        setError('Logout may not have completed properly');
      }
    } catch (error) {
      console.error('Logout error:', error.response?.data || error.message);
      setError('Logout failed. Please try again.');
    }
    console.log('Resetting auth state and user');
    setAuthState({ isAuthenticated: false, token: null, loading: false });
    setUser({});
    setError('');
    setIsLoggingOut(false);
    console.log('Navigating to /');
    navigate('/');
  };

  const updateUser = (userData) => {
    setUser({ ...user, ...userData });
  };

  const value = { authState, user, login, logout, updateUser, error, setUser ,axiUrl};
  return (
    <AppContext.Provider value={value}>
      <ToastContainer position="top-center" theme="dark"  hideProgressBar={true} transition={Flip} />
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);




